/**
 * The single source of truth for "which paths are reachable without a Vale
 * session" (VALE-1361).
 *
 * Two consumers gate on this, and they answer different questions:
 *
 * | Consumer | Question | Reads |
 * |---|---|---|
 * | `proxy.ts` | may this **request** through without a NextAuth session? | `PROXY_PUBLIC_PATHS` |
 * | `components/providers-wrapper.tsx` | may this **page tree** mount authenticated providers? | `PUBLIC_PAGE_PATHS` |
 *
 * They used to be two hand-typed arrays with a NOTE on each saying "edit both".
 * They had already drifted: the proxy carried twenty entries, the wrapper
 * thirteen, and three of the wrapper's (`/nextauth-login`, `/test-invitation`,
 * `/auth-redirect`) named routes that do not exist anywhere in `app/`. Adding a
 * public route to only one of them fails in a way no status code reveals — the
 * page 200s either way, and the wrapper's authenticated `/profile` fetch takes
 * the 401 (LF-3743, VALE-407 both landed after being bitten by exactly that).
 *
 * So the difference between the two lists is now **encoded**, not maintained:
 * the wrapper takes the page routes, the proxy takes the page routes plus the
 * non-page prefixes below. There is nothing left to keep in sync.
 *
 * **This list does not carry static assets.** Files under `public/` are exempt
 * at `proxy.ts`'s `config.matcher`, by construction rather than by name — see
 * the matcher's own comment. Three entries that existed purely to patch that
 * gap (`/favicon.ico`, `/reports`, `/pdf.worker.min.mjs`) are gone with it.
 *
 * Both consumers test with `String.prototype.startsWith`, so every entry is a
 * **prefix**. `/callback` deliberately does not cover
 * `/integrations/{smokeball,actionstep}/callback` — those are a different path,
 * and gating them is deliberate (LF-6390; see `proxy.ts`).
 */

/**
 * Public **page** routes — the surfaces a reader with no Vale session is meant
 * to land on and render. Shared verbatim by the proxy and the provider wrapper.
 *
 * Every entry here must resolve to a real route under `app/`;
 * `__tests__/public-paths-registry.test.ts` derives the route set from the
 * filesystem and reconciles it against this list in both directions.
 */
export const PUBLIC_PAGE_PATHS = [
  '/login',
  '/accept-invitation',
  // VALE-695: the impersonation landing page is visited with NO session — it
  // exists to establish one from a handoff code. Without this exemption the
  // authenticated profile fetch 401s and redirects to /login before the page
  // can redeem, which broke the handoff for the legacy token shape too.
  '/impersonate',
  '/get-started',
  '/auth-error',
  // The Keycloak OAuth callback forwarder (`app/(auth)/callback/route.ts`),
  // which really does live at `/callback`. It does NOT cover the integration
  // OAuth callbacks (`/integrations/{smokeball,actionstep}/callback`) — the
  // guard is a `startsWith` prefix test, so it never matched those.
  //
  // That is deliberate, not an oversight (LF-6390). Those pages complete the
  // token exchange through `handleSmokeballCallback` / `handleActionStepCallback`,
  // both of which call `validateAuthCookie()` and fail with "Authentication
  // required" without a session. Making them public would therefore trade one
  // failure for another while widening the authorisation surface. They stay
  // gated; `buildReturnTo` is what carries their `code`/`state` across the
  // login bounce.
  '/callback',
  // Beneficiary portal — public for external users, authenticated by the
  // portal JWT in the URL.
  '/portal',
  // LF-3743: AI Scribe QR-handoff mobile capture surface — token-in-URL
  // authentication via RecordingHandoffAuthGuard on the backend. The FE has no
  // NextAuth session, so mounting UserProfileProvider would fire an
  // authenticated profile fetch, take a 401, and bounce to /login.
  '/device-recording',
  // VALE-407: the public Microsoft 365 consent disclosure a firm's EXTERNAL
  // Microsoft administrator lands on, and its neutral dead-link sibling. The
  // reader has no Vale login and never will — the signed link the backend has
  // already validated is the entire credential, and the page holds no firm data
  // beyond the firm's name. Prefix covers /m365-authorisation/[token] and
  // /m365-authorisation/link-unavailable.
  '/m365-authorisation',
  // VALE-1779: the public single-sign-on consent disclosure and its three
  // outcome pages. Same reader and same reasoning as the Microsoft 365 pages
  // above, and a separate prefix because the two links live on different
  // records and authorise different things. Prefix covers
  // /sso-authorisation/[token], /complete, /not-completed and
  // /link-unavailable.
  '/sso-authorisation',
  // VALE-134: public, unauthenticated 2025 Probate Report lead-capture page.
  '/download-2025-probate-report',
  // VALE-1859: the post-logout landing pad Keycloak returns the browser to.
  // Public by construction, because the visitor has just been signed out of
  // both NextAuth and Keycloak and has no session left to present. Gated, it
  // bounced to `/login?callbackUrl=/signed-out/...` and the reason was lost
  // before the page could turn it back into the `?error=` card, which is the
  // whole job of the route. Prefix covers /signed-out/[reason].
  '/signed-out'
] as const;

/**
 * Prefixes the **proxy** additionally lets through, none of which is a page.
 *
 * The provider wrapper is a client component in the React tree — it only ever
 * sees a rendered page's pathname, so an API or framework prefix could never
 * reach it. That is why the two lists differ, and the difference is exactly
 * this array.
 */
export const PROXY_ONLY_PUBLIC_PATHS = [
  // NextAuth's own endpoints — the session/sign-in machinery itself.
  '/api/auth',
  // Portal API routes — authenticated via the portal JWT token.
  '/api/portal',
  // Portal API endpoints behind the binary proxy — same portal token.
  '/api/proxy/portal',
  // CSP violation report sink (LF-2982) — browsers POST without auth.
  '/api/csp-report',
  // LF-3743 — the QR-handoff capture endpoints, authenticated by the
  // handoff token: /api/device-recording/[token]/{,claim,upload-complete}.
  '/api/device-recording',
  // Next.js' own runtime routes. `_next/static` and `_next/image` never reach
  // the proxy at all (the matcher excludes them), but the RSC and data routes
  // under `/_next` do.
  '/_next',
  // RFC 8615 well-known URIs (Apple app-site association and friends). No
  // file extension, so the matcher's static-asset rule does not cover them.
  '/.well-known'
] as const;

/**
 * What `proxy.ts` gates on: the public pages plus the non-page prefixes.
 */
export const PROXY_PUBLIC_PATHS: readonly string[] = [
  ...PUBLIC_PAGE_PATHS,
  ...PROXY_ONLY_PUBLIC_PATHS
];
